import React, { useState, useEffect } from 'react';
import { Form, Button, Alert, Badge, FormCheck } from 'react-bootstrap';
import { updateAccount } from '../services/api';

const AccountStandardSize = ({ accountId, currentStandardSize, onClose, defaultStandardSize, onStandardSizeUpdate, autoStandardSize }) => {
  const [standardSize, setStandardSize] = useState(currentStandardSize?.toString() || '');
  const [useAutoStandardSize, setUseAutoStandardSize] = useState(autoStandardSize || false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Set initial state based on the database field
  useEffect(() => {
    setUseAutoStandardSize(autoStandardSize || false);
  }, [autoStandardSize]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      let value;
      if (useAutoStandardSize) {
        // Use the auto-calculated value
        value = defaultStandardSize;
      } else {
        // Use the custom input value
        value = standardSize === '' ? null : parseFloat(standardSize);
      }

      await updateAccount(accountId, {
        standard_size: value,
        auto_standard_size: useAutoStandardSize
      });
      
      // Call the callback to update parent component state
      if (onStandardSizeUpdate) {
        onStandardSizeUpdate(value, useAutoStandardSize);
      }
      
      // Close the modal
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update standard size');
    } finally {
      setLoading(false);
    }
  };

  const handleAutoToggle = (checked) => {
    setUseAutoStandardSize(checked);
    if (checked) {
      // When enabling auto mode, clear the custom input
      setStandardSize('');
    }
  };

  return (
    <div className="mb-4">
      <h4>
        Account Standard Size
        {!currentStandardSize && <Badge bg="info" className="ms-2">Default 1/10 of account value (rounded to nearest $1,000)</Badge>}
      </h4>
      <p className="text-muted">
        Standard size is used to calculate position sizes for each holding, helping to maintain portfolio balance.
      </p>
      
      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}

      <Form onSubmit={handleSubmit}>
        <Form.Group className="mb-3">
          <FormCheck
            type="switch"
            id="auto-standard-size-switch"
            label="Use automatic standard size (1/10 of account value, rounded to nearest $1,000)"
            checked={useAutoStandardSize}
            onChange={(e) => handleAutoToggle(e.target.checked)}
            className="mb-3"
          />
          
          {useAutoStandardSize && (
            <Alert variant="info" className="mb-3">
              <strong>Auto-calculated standard size:</strong> {defaultStandardSize ? `$${defaultStandardSize.toLocaleString()}` : 'Calculating...'}
              <br />
              <small className="text-muted">
                This value will automatically update when your account value changes.
              </small>
            </Alert>
          )}
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Custom Standard Size</Form.Label>
          <Form.Control
            type="number"
            step="0.1"
            min="0"
            placeholder="Enter custom standard size (e.g., 1.0)"
            value={standardSize}
            onChange={(e) => setStandardSize(e.target.value)}
            disabled={useAutoStandardSize}
          />
          <Form.Text className="text-muted">
            {useAutoStandardSize 
              ? "Custom standard size is disabled when using automatic calculation."
              : "Enter a custom value or use the automatic calculation above. A position with the same value as your standard size equals 1.0x position size."
            }
          </Form.Text>
        </Form.Group>

        <div className="d-flex gap-2">
          <Button 
            variant="outline-secondary"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button 
            variant="primary" 
            type="submit"
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save Standard Size'}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default AccountStandardSize; 